import { Link } from "react-router-dom";
import { Clock, MapPin, MessageCircle, Instagram, ArrowRight, BadgeCheck, Flame } from "lucide-react";
import { SITE, HOURS, BRANCHES, SIGNATURES } from "../data/site";
import { Seal, SectionHead, Marquee, VertKanji } from "../components/Chrome";

function Enso() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 200 200"
      className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 text-ink opacity-[0.07]"
    >
      <circle
        cx="100"
        cy="100"
        r="78"
        fill="none"
        stroke="currentColor"
        strokeWidth="16"
        strokeLinecap="round"
        strokeDasharray="430 60"
        transform="rotate(-40 100 100)"
      />
    </svg>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-paper" data-testid="hero-section">
      <Enso />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-14 sm:px-6 lg:grid-cols-12 lg:px-12 lg:pb-24 lg:pt-24">
        <div className="lg:col-span-7">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-soy">
            <span aria-hidden="true" className="inline-block h-2 w-2 bg-hanko" />
            Kedai Sushi Jepang · Bersertifikat Halal MUI
          </p>
          <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Umaku Sushi
            <span className="mt-4 block font-sans text-xl font-semibold leading-snug tracking-normal text-sumi sm:text-2xl">
              Sashimi potongan tebal, signature roll, dan ramen — <span className="text-hanko">100% halal</span> di Jabodetabek.
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-sumi">
            Irisan salmon tebal, nasi hangat, dan umami yang jujur. Umaku Sushi menghadirkan hidangan Jepang
            autentik bersertifikat halal MUI — kini hadir di Bintaro, Cibubur, Bekasi, dan Jakarta Selatan.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/menu/"
              data-testid="hero-menu-cta"
              className="inline-flex items-center gap-2 bg-ink px-6 py-3.5 text-sm font-bold text-paper transition-colors duration-200 hover:bg-hanko"
            >
              Lihat Menu
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a
              href={SITE.whatsappBintaro}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="inline-flex items-center gap-2 border-2 border-ink px-6 py-3 text-sm font-bold text-ink transition-colors duration-200 hover:bg-ink hover:text-paper"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              Pesan via WhatsApp
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-sumi">
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-hanko" aria-hidden="true" />
              {HOURS.summary}
            </span>
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-hanko" aria-hidden="true" />
              4 cabang di Jabodetabek
            </span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative">
            <img
              src="/images/sashimi-salmon.jpg"
              alt="Sashimi salmon segar dengan potongan tebal khas Umaku Sushi"
              width="1200"
              height="1600"
              fetchPriority="high"
              data-testid="hero-image"
              className="aspect-[3/4] w-full border-2 border-ink object-cover"
            />
            <div aria-hidden="true" className="absolute inset-0 -z-10 translate-x-4 translate-y-4 bg-salmon/30" />
            <Seal className="absolute -bottom-6 -left-4 h-20 w-20 -rotate-6 text-3xl sm:-left-6" />
            <VertKanji
              text="旨く寿司"
              className="absolute -right-3 top-8 border-2 border-ink bg-paper px-2 py-4 text-xl font-bold text-ink shadow-[4px_4px_0_rgba(24,24,27,1)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Signature() {
  return (
    <section className="bg-paper py-16 lg:py-24" data-testid="signature-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead overline="Menu Andalan" title="Yang Wajib Dipesan" jp="看板" />
          <Link
            to="/menu/"
            data-testid="signature-menu-link"
            className="inline-flex items-center gap-2 border-b-2 border-hanko pb-1 text-sm font-bold text-ink transition-colors hover:text-hanko"
          >
            Lihat menu lengkap
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        <ol className="mt-10 grid gap-x-12 md:grid-cols-2">
          {SIGNATURES.map((item, i) => (
            <li
              key={item.name}
              data-testid={`signature-item-${i + 1}`}
              className="group flex gap-5 border-b border-sand py-6 transition-colors duration-200 hover:bg-parchment/60"
            >
              <span aria-hidden="true" className="font-display text-2xl font-bold text-salmon/70 transition-colors group-hover:text-hanko">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-ink">
                  {item.name}
                  <span className="ml-2 align-middle text-xs font-medium tracking-widest text-sumi/70">{item.jp}</span>
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-sumi">{item.desc}</p>
                {item.tag ? (
                  <span className="mt-2.5 inline-block border border-hanko/40 bg-hanko/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-hanko">
                    {item.tag}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function WhyUmaku() {
  const points = [
    {
      kanji: "厚",
      title: "Potongan Tebal",
      desc: "Sashimi dan topping diiris tebal — setiap suapan terasa memuaskan, sesuai reputasi Umaku.",
    },
    {
      kanji: "鮮",
      title: "Bahan Segar",
      desc: "Umaku hanya mengandalkan kesegaran bahan dan umami alami, tanpa berlebihan pada bumbu.",
    },
    {
      kanji: "印",
      title: "100% Halal MUI",
      desc: "Seluruh hidangan bersertifikat halal dari MUI, sehingga semua orang bisa menikmati dengan tenang.",
    },
  ];
  return (
    <section className="border-y-2 border-ink bg-parchment py-16 lg:py-24" data-testid="why-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <SectionHead overline="Kenapa Umaku" title="Tiga Hal yang Kami Pegang" jp="旨く" />
        <div className="mt-10 grid gap-px border-2 border-ink bg-ink md:grid-cols-3">
          {points.map((p) => (
            <article key={p.title} data-testid={`why-${p.title}`} className="relative overflow-hidden bg-paper p-8">
              <span aria-hidden="true" className="absolute -right-3 -top-6 font-display text-8xl font-bold text-salmon/15">
                {p.kanji}
              </span>
              <BadgeCheck className="h-6 w-6 text-hanko" aria-hidden="true" />
              <h3 className="mt-4 font-display text-xl font-bold text-ink">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-sumi">{p.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function UmamiQuote() {
  return (
    <section className="bg-ink py-16 text-paper lg:py-24" data-testid="umami-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <img
            src="/images/sushi-extra-1.jpg"
            alt="Nigiri salmon diapit sumpit kayu"
            width="1200"
            height="1500"
            loading="lazy"
            data-testid="umami-image"
            className="aspect-[4/5] w-full border-2 border-paper/20 object-cover"
          />
        </div>
        <div className="lg:col-span-7">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-salmon">
            <Flame className="h-4 w-4" aria-hidden="true" />
            Filosofi Rasa
          </p>
          <blockquote className="mt-5 font-display text-2xl font-bold leading-snug sm:text-3xl lg:text-4xl">
            “Kami hanya mengandalkan <span className="text-salmon">umami</span> — rasa gurih alami dari bahan segar,
            sama seperti kedai sushi di negeri asalnya.”
          </blockquote>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-paper/70">
            Tanpa klaim berlebihan: bahan segar, potongan besar, dan penyajian sederhana khas kedai Jepang.
            Suasana tiap cabang dibuat simpel dan minimalis, agar fokus tetap pada hidangan di meja Anda.
          </p>
          <Link
            to="/tentang/"
            data-testid="umami-about-link"
            className="mt-8 inline-flex items-center gap-2 border-b-2 border-salmon pb-1 text-sm font-bold text-paper transition-colors hover:text-salmon"
          >
            Kenali Umaku lebih dekat
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function BranchStrip() {
  return (
    <section className="bg-paper py-16 lg:py-24" data-testid="branches-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead overline="Lokasi Cabang" title="Temukan Umaku Terdekat" jp="店舗" />
          <Link
            to="/lokasi/"
            data-testid="branches-lokasi-link"
            className="inline-flex items-center gap-2 border-b-2 border-hanko pb-1 text-sm font-bold text-ink transition-colors hover:text-hanko"
          >
            Detail lokasi & jam buka
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        <ul className="mt-10 divide-y divide-sand border-y border-sand">
          {BRANCHES.map((b, i) => (
            <li key={b.id} data-testid={`home-branch-${b.id}`}>
              <Link
                to="/lokasi/"
                className="group flex flex-wrap items-baseline gap-x-6 gap-y-1 py-5 transition-colors duration-200 hover:bg-parchment/60"
              >
                <span aria-hidden="true" className="font-display text-xl font-bold text-salmon/70 group-hover:text-hanko">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-xl font-bold text-ink">{b.area}</span>
                <span className="text-sm text-sumi">{b.city}</span>
                {b.isNew ? (
                  <span className="border border-wasabi/50 bg-wasabi/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest text-wasabi">
                    Cabang Baru
                  </span>
                ) : null}
                <ArrowRight
                  className="ml-auto h-4 w-4 text-ink transition-transform duration-200 group-hover:translate-x-1 group-hover:text-hanko"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="border-t-2 border-ink bg-parchment py-16 lg:py-20" data-testid="cta-section">
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-12">
        <div className="flex items-start gap-5">
          <Seal className="h-14 w-14 shrink-0 rotate-3 text-2xl" />
          <div>
            <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Sudah lapar?</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-sumi">
              Pesan untuk dine-in atau take-away melalui WhatsApp cabang Bintaro, atau cek kabar terbaru di Instagram kami.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <a
            href={SITE.whatsappBintaro}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp"
            className="inline-flex items-center gap-2 bg-hanko px-6 py-3.5 text-sm font-bold text-paper transition-colors duration-200 hover:bg-[#a82719]"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            WhatsApp Bintaro
          </a>
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-instagram"
            className="inline-flex items-center gap-2 border-2 border-ink px-6 py-3 text-sm font-bold text-ink transition-colors duration-200 hover:bg-ink hover:text-paper"
          >
            <Instagram className="h-4 w-4" aria-hidden="true" />
            {SITE.instagramHandle}
          </a>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <Hero />
      <Marquee items={["Sashimi", "Signature Roll", "Ramen", "Donburi", "Salad", "旨く Umaku", "100% Halal MUI"]} />
      <Signature />
      <WhyUmaku />
      <UmamiQuote />
      <BranchStrip />
      <CtaBand />
    </>
  );
}
